import * as XLSX from 'xlsx';
import {
  addDays, differenceInCalendarDays, endOfMonth, endOfWeek, endOfYear, format, isValid, parseISO,
  startOfDay, startOfMonth, startOfWeek, startOfYear, subMonths
} from 'date-fns';

// Filter values shared by the two reports and the URL (?report_job=, ?report_stage=).
export const ALL = 'all';
// Same sentinel as REPORT_NO_JOB in recruitment_routes.py.
export const NO_JOB = '__none__';
export const NO_STAGE = '__nostage__';

export const JOB_STATUS_COLORS = {
  'Open': 'bg-green-100 text-green-700',
  'Closed': 'bg-red-100 text-red-700',
  'On Hold': 'bg-amber-100 text-amber-700'
};

// Active jobs first wherever reports list jobs.
const JOB_STATUS_ORDER = { 'Open': 0, 'On Hold': 1, 'Closed': 2 };
export const jobStatusRank = (status) => JOB_STATUS_ORDER[status] ?? 3;

// First column of a wide report table: stays put while the rest scrolls
// sideways. The inset shadow draws its edge; a border would not stay with a
// sticky cell under border-collapse.
export const STICKY_COL = 'sticky left-0 z-10 shadow-[inset_-1px_0_0_rgb(226,232,240)]';

// A plain left click navigates in place; modified and middle clicks are left
// to the browser so "open in new tab" keeps working on report links.
export const isPlainLeftClick = (e) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0);

// Stored timestamps are UTC ISO strings, interview times local ones without
// a zone; parseISO reads each as what it is.
export const toDate = (value) => {
  if (!value) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
};

// An interview's recommendation as text colour, wherever rounds are listed.
export const RECOMMENDATION_COLORS = {
  'Strong Hire': 'text-emerald-700',
  'Hire': 'text-green-700',
  'No Hire': 'text-red-700',
  'Hold': 'text-amber-700'
};

export const formatDate = (value, pattern = 'dd MMM yyyy') => {
  const d = toDate(value);
  return d ? format(d, pattern) : '';
};

// Whole days from `value` to today; null when there is no usable date.
export const daysSince = (value) => {
  const d = toDate(value);
  return d ? Math.max(0, differenceInCalendarDays(new Date(), d)) : null;
};

// ---- Report periods ----
// A period is a first and a last day, both included, as local 'yyyy-MM-dd'
// strings; ALL leaves that side open. They go in the URL as dates rather than
// as "this month", so a shared link shows the same days whenever it is opened.
export const toDay = (date) => format(date, 'yyyy-MM-dd');
export const isDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || '') && isValid(parseISO(value));
export const hasPeriod = (from, to) => from !== ALL || to !== ALL;

export const PERIOD_PRESETS = [
  { key: 'this_week', label: 'This week', range: (t) => [startOfWeek(t, { weekStartsOn: 1 }), endOfWeek(t, { weekStartsOn: 1 })] },
  { key: 'this_month', label: 'This month', range: (t) => [startOfMonth(t), endOfMonth(t)] },
  { key: 'last_month', label: 'Last month', range: (t) => [startOfMonth(subMonths(t, 1)), endOfMonth(subMonths(t, 1))] },
  { key: 'last_3_months', label: 'Last 3 months', range: (t) => [addDays(subMonths(t, 3), 1), t] },
  { key: 'this_year', label: 'This year', range: (t) => [startOfYear(t), endOfYear(t)] }
];

export const presetDays = (key) => PERIOD_PRESETS.find((p) => p.key === key).range(new Date()).map(toDay);

// The preset whose days are exactly these today, if any.
export const presetFor = (from, to) => (
  PERIOD_PRESETS.find((p) => {
    const [first, last] = presetDays(p.key);
    return first === from && last === to;
  })?.key || null
);

// The API takes instants: the start of the first day and the start of the day
// after the last, in the browser's time zone, so "1 Oct" is the user's 1 Oct.
export const periodBounds = (from, to) => ({
  start: from !== ALL ? startOfDay(parseISO(from)).toISOString() : null,
  end: to !== ALL ? startOfDay(addDays(parseISO(to), 1)).toISOString() : null
});

export const formatPeriod = (from, to) => {
  const first = from !== ALL ? formatDate(from) : null;
  const last = to !== ALL ? formatDate(to) : null;
  if (first && last) return first === last ? first : `${first} – ${last}`;
  if (first) return `From ${first}`;
  if (last) return `Up to ${last}`;
  return 'All time';
};

// "between 01 Oct 2026 and 31 Oct 2026", for sentences about a period.
export const periodPhrase = (from, to) => {
  const first = from !== ALL ? formatDate(from) : null;
  const last = to !== ALL ? formatDate(to) : null;
  if (first && last) return first === last ? `on ${first}` : `between ${first} and ${last}`;
  if (first) return `since ${first}`;
  return last ? `up to ${last}` : 'at any time';
};

export const periodFileTag = (from, to) => {
  if (!hasPeriod(from, to)) return '';
  if (from !== ALL && to !== ALL) return `${from}_to_${to}`;
  return from !== ALL ? `from_${from}` : `to_${to}`;
};

// Excel caps sheet names at 31 characters, forbids : \ / ? * [ ] and wants
// them unique within the workbook.
const sheetName = (name, used) => {
  const base = (String(name || 'Sheet').replace(/[:\\/?*[\]]/g, ' ').trim() || 'Sheet').slice(0, 31);
  let candidate = base;
  for (let n = 2; used.has(candidate.toLowerCase()); n += 1) {
    const suffix = ` (${n})`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
  }
  used.add(candidate.toLowerCase());
  return candidate;
};

export const fileSafe = (text) => String(text || '').replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '_');

// sheets: [{ name, rows (array of arrays, header first), widths?, filter?, note? }]
// `filter` adds Excel's filter dropdowns to the header row. `note` is a line
// above the table saying what it covers (e.g. the period), so the file still
// says so after it is renamed or forwarded.
export const downloadWorkbook = (sheets, filename) => {
  const workbook = XLSX.utils.book_new();
  const used = new Set();
  sheets.forEach(({ name, rows, widths, filter, note }) => {
    const sheet = XLSX.utils.aoa_to_sheet(note ? [[note], [], ...rows] : rows);
    if (widths) sheet['!cols'] = widths.map((wch) => ({ wch }));
    if (filter && sheet['!ref']) {
      const range = XLSX.utils.decode_range(sheet['!ref']);
      if (note) range.s.r = 2; // the header row, below the note and a blank row
      sheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
    }
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName(name, used));
  });
  XLSX.writeFile(workbook, filename);
};
